import React, { useEffect, useState } from 'react';
import { fmtWh } from './time';
import { useParams, useNavigate } from 'react-router-dom';
import { authFetch, API } from './api';
import UsersInfo from './UsersInfo';
import { ACCENTS, applyAppearance, cachedPrefs, AppearancePrefs } from './appearance';
import { Branding, cachedFavicon, cachedLogo, storeBranding, toSquarePng } from './branding';
import StoresPage from './StoresPage';
import ShippingSettings from './ShippingSettings';
import WarehouseSettings, { WAREHOUSE_TABS, WarehouseSub } from './warehouse/WarehouseSettings';
import ConnectorsTab from './ConnectorsSettings';
import NotificationsTab from './NotificationsSettings';
import CustomerMailsTab from './CustomerMailsSettings';
import BadgesRightsSettings from './pp2/BadgesRightsSettings';
import PrintingSettings from './pp2/PrintingSettings';
import BeginnerCard from './pp2/BeginnerCard';
import AnnouncementsSettings from './pp2/AnnouncementsSettings';
import HrxSettings from './pp2/HrxSettings';
import InboundSettingsCard from './inbound/InboundSettingsCard';

// ── Packing settings ──────────────────────────────────────────────────────────

const PACKING_DEFAULTS = { delay: 3, manualMode: false };

function PackingTab() {
  const [settings, setSettings] = useState(() => {
    const s = JSON.parse(localStorage.getItem('wms_packing_settings') || '{}');
    return {
      delay: s.delay ?? PACKING_DEFAULTS.delay,
      manualMode: s.manualMode ?? PACKING_DEFAULTS.manualMode,
    };
  });

  const update = (patch: Partial<typeof settings>) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    localStorage.setItem('wms_packing_settings', JSON.stringify(next));
  };

  return (
    <div>
      <p className="text-gray-500 text-sm mb-6">Configure how the packing screen behaves during a packing session.</p>
      <div className="mb-6"><BeginnerCard /></div>
      <div className="space-y-3">
        <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-1 px-1">Orders</div>

        <div className={`bg-[#1a1d27] rounded-xl border border-gray-800 px-5 py-4 flex items-center justify-between transition-opacity ${settings.manualMode ? 'opacity-40' : ''}`}>
          <div>
            <div className="text-white text-sm font-medium">Next order in packing in</div>
            <div className="text-gray-500 text-xs mt-0.5">Automatically move to the next order after all items are scanned</div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number" min={1} max={60}
              value={settings.delay}
              onChange={e => update({ delay: Math.max(1, parseInt(e.target.value) || 1) })}
              disabled={settings.manualMode}
              className="w-20 bg-gray-800 border border-gray-700 text-white text-center text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500 disabled:cursor-not-allowed"
            />
            <span className="text-gray-500 text-sm">seconds</span>
          </div>
        </div>

        <div className="bg-[#1a1d27] rounded-xl border border-gray-800 px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-white text-sm font-medium">Manual mode</div>
            <div className="text-gray-500 text-xs mt-0.5">Disables auto-advance — next order only moves when the Next button is clicked</div>
          </div>
          <button
            onClick={() => update({ manualMode: !settings.manualMode })}
            className={`relative w-12 h-6 rounded-full transition-colors ${settings.manualMode ? 'bg-blue-600' : 'bg-gray-700'}`}
          >
            <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all duration-200 ${settings.manualMode ? 'left-7' : 'left-1'}`} />
          </button>
        </div>
      </div>
      <HandleFirstTexts />
    </div>
  );
}

// The wording the Orders page uses for "handle first" tabs (tabs_config.urgent):
// the notice above the table and the prompt before a new round. Server-side
// (app_settings.packing_texts) — unlike the two local toggles above, this is
// read by every packer's screen, so it cannot live in one browser's storage.
function HandleFirstTexts() {
  type Texts = { urgent_notice: string; urgent_prompt: string };
  const [texts, setTexts] = useState<Texts | null>(null);
  const [defaults, setDefaults] = useState<Texts | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState('');
  const canEdit = ['admin', 'manager'].includes(localStorage.getItem('wms_role') || '');

  useEffect(() => {
    authFetch('/api/packing-texts').then(async r => {
      if (!r.ok) { setErr('Could not load the texts'); return; }
      const d = await r.json();
      setTexts({ urgent_notice: d.urgent_notice, urgent_prompt: d.urgent_prompt });
      setDefaults(d.defaults);
    });
  }, []);

  const save = async () => {
    if (!texts) return;
    setSaving(true); setErr(''); setSaved(false);
    const r = await authFetch('/api/packing-texts', { method: 'PUT', body: JSON.stringify(texts) });
    setSaving(false);
    if (r.ok) { setSaved(true); setTimeout(() => setSaved(false), 2500); }
    else { const d = await r.json().catch(() => ({})); setErr(d.detail || 'Could not save'); }
  };

  if (!texts) return null;
  const preview = (t: string) => t.replace(/\{tab\}/g, 'Product exchange').replace(/\{count\}/g, '2');
  const field = (key: keyof Texts, title: string, help: string) => (
    <div className="bg-[#1a1d27] rounded-xl border border-gray-800 px-5 py-4">
      <div className="text-white text-sm font-medium">{title}</div>
      <div className="text-gray-500 text-xs mt-0.5 mb-2">{help}</div>
      <textarea value={texts[key]} disabled={!canEdit} rows={3}
        onChange={e => setTexts({ ...texts, [key]: e.target.value })}
        className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500 disabled:opacity-60" />
      <div className="mt-2 text-xs text-gray-500">Shown as:</div>
      <div className="mt-1 px-3 py-2 rounded-lg border-l-4 border-red-600 bg-red-950/30 text-sm text-red-100 whitespace-pre-line">
        {preview(texts[key])}
      </div>
      {defaults && texts[key] !== defaults[key] && canEdit && (
        <button onClick={() => setTexts({ ...texts, [key]: defaults[key] })}
          className="mt-2 text-xs text-gray-500 hover:text-white">Back to the default wording</button>
      )}
    </div>
  );

  return (
    <div className="mt-8">
      <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-1 px-1">Handle-first orders</div>
      <p className="text-gray-500 text-xs mb-3 px-1">
        Tabs marked “handle first” under Settings → Tabs (Product exchange today) show their count on red and
        these two texts. <span className="text-gray-400">{'{tab}'}</span> is the tab's name,{' '}
        <span className="text-gray-400">{'{count}'}</span> how many are waiting.
      </p>
      {err && <div className="mb-3 text-red-400 text-sm bg-red-900/20 border border-red-800/40 rounded-lg px-4 py-2">{err}</div>}
      <div className="space-y-3">
        {field('urgent_notice', 'Notice on the tab',
               'Shown between the filter buttons and the order table while the tab has orders.')}
        {field('urgent_prompt', 'Prompt before a new round',
               'Asked when orders are moved to Printed while handle-first orders are still waiting. Continuing is allowed and recorded on the round.')}
      </div>
      {canEdit && (
        <div className="mt-3 flex items-center gap-3">
          <button onClick={save} disabled={saving}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg">
            {saving ? 'Saving…' : 'Save texts'}
          </button>
          {saved && <span className="text-green-400 text-sm">Saved — every packer sees it on their next page load</span>}
        </div>
      )}
    </div>
  );
}

// ── Users ─────────────────────────────────────────────────────────────────────

interface User {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  role: string;
  created_at: string;
  active?: boolean;
  employment_start?: string | null;
  employment_end?: string | null;
  contract_type?: string | null;
  end_reason?: string | null;
  removed_at?: string | null;
  // Set when this login belongs to someone who already has an account here. Their employment,
  // hours and statistics all live on that person, not on this login.
  primary_username?: string | null;
}

function userDisplayName(u: User): string {
  const full = [u.first_name, u.last_name].filter(Boolean).join(' ');
  return full || u.username;
}

interface UserHistory { history: Record<string, number>; purgeable: boolean; total_rows: number; }

const ROLE_COLORS: Record<string, string> = {
  admin:   'bg-blue-900/60 text-blue-400',
  manager: 'bg-purple-900/60 text-purple-400',
  staff:   'bg-gray-700 text-gray-400',
  viewer:  'bg-gray-800 text-gray-500',
};

function UsersTab() {
  const currentUsername = localStorage.getItem('wms_username') || '';
  const currentRole     = localStorage.getItem('wms_role') || 'staff';
  const canManage       = currentRole === 'admin' || currentRole === 'manager';

  const [users, setUsers] = useState<User[]>([]);

  // Reset target password
  const [resetTarget, setResetTarget] = useState<string | null>(null);
  const [resetPw, setResetPw]         = useState('');
  const [resetError, setResetError]   = useState('');

  // Add user
  const [adding, setAdding]           = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newFirst, setNewFirst]       = useState('');
  const [newLast, setNewLast]         = useState('');
  const [newEmail, setNewEmail]       = useState('');
  const [newPhone, setNewPhone]       = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole]         = useState('staff');
  const [addError, setAddError]       = useState('');
  // '' = a new person. Otherwise the username of the person this login belongs to, so their work
  // lands in one place instead of being split across two accounts.
  const [newPrimary, setNewPrimary]   = useState('');

  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [confirmDeact, setConfirmDeact] = useState<string | null>(null);
  const [usersView, setUsersView] = useState<'list' | 'info'>('list');
  const [histFor, setHistFor] = useState<Record<string, UserHistory>>({});
  const [actErr, setActErr] = useState<string | null>(null);

  // Edit user
  const [editTarget, setEditTarget]   = useState<string | null>(null);
  const [editUsername, setEditUsername] = useState('');
  const [editFirst, setEditFirst]     = useState('');
  const [editLast, setEditLast]       = useState('');
  const [editEmail, setEditEmail]     = useState('');
  const [editPhone, setEditPhone]     = useState('');
  const [editRole, setEditRole]       = useState('');
  const [editError, setEditError]     = useState('');

  const load = () => {
    authFetch(`${API}/users`).then(r => r.ok ? r.json() : []).then(data => {
      if (Array.isArray(data)) setUsers(data);
    });
  };

  useEffect(() => { if (canManage) load(); }, [canManage]);

  const handleResetPassword = async (target: string) => {
    setResetError('');
    if (!resetPw || resetPw.length < 8) { setResetError('Min 8 characters'); return; }
    const res = await authFetch(`${API}/users/${encodeURIComponent(target)}/password`, {
      method: 'PUT',
      body: JSON.stringify({ new_password: resetPw }),
    });
    if (!res.ok) { const d = await res.json(); setResetError(d.detail || 'Error'); return; }
    setResetTarget(null); setResetPw('');
  };

  const handleAdd = async () => {
    setAddError('');
    if (!newUsername || !newPassword) { setAddError('Username and password are required'); return; }
    if (newPassword.length < 8) { setAddError('Password must be at least 8 characters'); return; }
    const res = await authFetch(`${API}/users`, {
      method: 'POST',
      body: JSON.stringify({
        username: newUsername,
        first_name: newFirst || undefined,
        last_name: newLast || undefined,
        email: newEmail || undefined,
        phone: newPhone || undefined,
        password: newPassword,
        role: newRole,
        primary_username: newPrimary || undefined,
      }),
    });
    if (!res.ok) { const d = await res.json(); setAddError(d.detail || 'Error'); return; }
    setNewUsername(''); setNewFirst(''); setNewLast(''); setNewEmail(''); setNewPhone('');
    setNewPassword(''); setNewRole('staff'); setNewPrimary(''); setAdding(false);
    load();
  };

  // Attach an existing account to a person, or detach it back into its own person. Admin only:
  // it decides whose work time and whose statistics this account's activity lands in.
  const setPrimary = async (target: string, primary: string) => {
    setActErr(null);
    const res = await authFetch(`${API}/users/${encodeURIComponent(target)}`, {
      method: 'PUT',
      body: JSON.stringify({ primary_username: primary || null }),
    });
    if (!res.ok) { const d = await res.json(); setActErr(d.detail || 'Error'); return; }
    load();
  };

  // Nothing here deletes work history. Deactivating keeps every row and keeps the username
  // claimed, so a future hire can never inherit someone else's picked/packed record.
  const openDeact = async (username: string) => {
    setConfirmDeact(username); setConfirmDelete(null); setActErr(null);
    const r = await authFetch(`${API}/users/${encodeURIComponent(username)}/history-summary`);
    if (r.ok) {
      const summary: UserHistory = await r.json();
      setHistFor(h => ({ ...h, [username]: summary }));
    }
  };

  const act = async (username: string, what: 'deactivate' | 'reactivate') => {
    setActErr(null);
    const r = await authFetch(`${API}/users/${encodeURIComponent(username)}/${what}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
    });
    if (!r.ok) {
      let m = `${r.status}`;
      try { m = (await r.json()).detail || m; } catch { /* keep status */ }
      setActErr(m); return;
    }
    setConfirmDeact(null);
    load();
  };

  // Removes the account from the lists and closes employment. History is never touched — the
  // backend keeps the (hidden) users row so statistics still resolve their name and the username
  // can never be reused. Only a history-free account is really deleted.
  const handleDelete = async (username: string) => {
    setActErr(null);
    const r = await authFetch(`${API}/users/${encodeURIComponent(username)}`, { method: 'DELETE' });
    if (!r.ok) {
      let m = `${r.status}`;
      try { m = (await r.json()).detail || m; } catch { /* keep status */ }
      setActErr(m); return;
    }
    setConfirmDelete(null); setConfirmDeact(null);
    load();
  };

  const openDelete = async (username: string) => {
    setConfirmDelete(username); setConfirmDeact(null); setActErr(null);
    const r = await authFetch(`${API}/users/${encodeURIComponent(username)}/history-summary`);
    if (r.ok) {
      const summary: UserHistory = await r.json();
      setHistFor(h => ({ ...h, [username]: summary }));
    }
  };

  const openEdit = (u: User) => {
    setEditTarget(u.username);
    setEditUsername(u.username);
    setEditFirst(u.first_name || '');
    setEditLast(u.last_name || '');
    setEditEmail(u.email || '');
    setEditPhone(u.phone || '');
    setEditRole(u.role);
    setEditError('');
    setResetTarget(null);
  };

  // Fields are sent as-is (empty string clears them) — the backend only writes the keys it
  // receives. Renaming rewrites the person's whole history server-side and signs them out.
  const handleSaveEdit = async (target: string) => {
    setEditError('');
    const renaming = editUsername.trim() !== target;
    if (renaming && !window.confirm(
      `Rename "${target}" to "${editUsername.trim()}"?\n\n` +
      `Their picking, packing, work-hour and absence history moves to the new name, and they ` +
      `will be signed out and must sign in again with it.`)) return;
    const res = await authFetch(`${API}/users/${encodeURIComponent(target)}`, {
      method: 'PUT',
      body: JSON.stringify({
        username: editUsername.trim(),
        first_name: editFirst,
        last_name: editLast,
        email: editEmail,
        phone: editPhone,
        role: editRole,
      }),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      setEditError(d.detail || 'Error'); return;
    }
    // Renaming yourself invalidates your own token, since it carries the old name.
    if (renaming && target === currentUsername) {
      const d = await res.json().catch(() => ({}));
      alert(`Your username is now "${d.username || editUsername.trim()}". Please sign in again.`);
      localStorage.removeItem('wms_token');
      window.location.reload();
      return;
    }
    setEditTarget(null);
    load();
  };

  const roleOptions = currentRole === 'admin'
    ? ['staff', 'viewer', 'manager', 'admin']
    : ['staff', 'viewer'];

  return (
    <div className="space-y-6">

      {/* List / Info sub-tabs. Info explains what Deactivate and Delete actually do, since the
          difference matters and is hard to work out from the buttons alone. */}
      {canManage && (
        <div className="flex gap-1 border-b border-gray-800">
          {([['list', 'Users'], ['info', 'Info']] as const).map(([k, lbl]) => (
            <button key={k} onClick={() => setUsersView(k)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                usersView === k ? 'text-white border-b-2 border-blue-500' : 'text-gray-400 hover:text-white'
              }`}>
              {lbl}
            </button>
          ))}
        </div>
      )}

      {canManage && usersView === 'info' && <UsersInfo />}

      {/* User list — admin/manager only */}
      {canManage && usersView === 'list' && (
        <div>
          <p className="text-gray-500 text-sm mb-4">
            {currentRole === 'admin' ? 'Manage all accounts.' : 'Manage staff and viewer accounts.'}
            {' '}Everyone here is currently with us — people who have left are hidden but keep all
            their history. See the Info tab for what each button does.
          </p>
          <div className="space-y-2 mb-4">
            {users.map(u => (
              <div key={u.id} className="bg-[#1a1d27] rounded-xl border border-gray-800 px-5 py-3">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white font-medium text-sm">{userDisplayName(u)}</span>
                      {userDisplayName(u) !== u.username && (
                        <span className="text-gray-600 text-xs">@{u.username}</span>
                      )}
                      <span className={`text-xs px-2 py-0.5 rounded-full ${ROLE_COLORS[u.role] || ROLE_COLORS.staff}`}>
                        {u.role}
                      </span>
                      {u.active === false && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-900/50 text-amber-400"
                              title="Cannot log in. History kept.">
                          deactivated
                        </span>
                      )}
                      {u.employment_start && !u.primary_username && (
                        <span className="text-gray-600 text-[11px]">
                          {u.employment_start}{u.employment_end ? ` → ${u.employment_end}` : ' → present'}
                        </span>
                      )}
                      {u.primary_username && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-900/40 text-blue-300"
                              title={`A second login for ${u.primary_username}. Employment, hours and `
                                     + `statistics belong to that person; work done here counts as theirs.`}>
                          2nd login for {u.primary_username}
                        </span>
                      )}
                    </div>
                    <div className="text-gray-500 text-xs mt-0.5">{u.email || '—'}</div>
                    {/* Re-link an account that already exists. The person model only works if a
                        mistake can be corrected without deleting and recreating a login. */}
                    {currentRole === 'admin' && editTarget === u.username && (
                      <div className="mt-2">
                        <label className="text-gray-400 text-xs uppercase tracking-wider">Who is this for</label>
                        <select value={u.primary_username || ''}
                          onChange={e => setPrimary(u.username, e.target.value)}
                          className="mt-1 w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500">
                          <option value="">Their own person</option>
                          {users.filter(o => o.username !== u.username && !o.primary_username && !o.removed_at)
                                .map(o => (
                            <option key={o.username} value={o.username}>
                              A second login for {userDisplayName(o)}
                            </option>
                          ))}
                        </select>
                        <p className="text-[11px] text-gray-600 mt-1">
                          Attaching this login to someone folds it into that person: one employment,
                          one set of hours, and work done on either account counts as theirs.
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 w-full md:w-auto">
                    {editTarget !== u.username && resetTarget !== u.username && (
                      <button onClick={() => openEdit(u)}
                        className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white text-xs rounded-lg transition-colors">
                        Edit
                      </button>
                    )}
                    {editTarget !== u.username && resetTarget !== u.username && (
                      <button onClick={() => { setResetTarget(u.username); setResetPw(''); setResetError(''); setEditTarget(null); }}
                        className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white text-xs rounded-lg transition-colors">
                        Reset password
                      </button>
                    )}
                    {u.username !== currentUsername && (
                      <>
                        {u.active === false
                          ? <button onClick={() => act(u.username, 'reactivate')}
                              className="px-3 py-1.5 bg-emerald-800/70 hover:bg-emerald-700 text-white text-xs rounded-lg transition-colors"
                              title="Restore access">
                              Reactivate
                            </button>
                          : <button onClick={() => openDeact(u.username)}
                              className="px-3 py-1.5 bg-gray-800 hover:bg-amber-900/50 text-gray-400 hover:text-amber-300 text-xs rounded-lg transition-colors"
                              title="Pause access — they stay employed and stay in this list">
                              Deactivate
                            </button>}
                        <button onClick={() => openDelete(u.username)}
                          className="px-3 py-1.5 bg-gray-800 hover:bg-red-900/50 text-gray-400 hover:text-red-400 text-xs rounded-lg transition-colors"
                          title="Remove from the lists and end employment. History is kept.">
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {confirmDeact === u.username && (
                  <div className="mt-3 pt-3 border-t border-gray-800">
                    <p className="text-sm text-gray-300">
                      Pause <span className="text-white font-medium">{userDisplayName(u)}</span>?
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      They cannot log in and any open session ends within a minute.
                      <span className="text-gray-400"> They stay employed and stay in this list</span>, so
                      you can switch them back on when they next work. Use this for someone who works a
                      few days a month — not for someone who has left.
                    </p>
                    {actErr && <p className="text-xs text-red-400 mt-2">{actErr}</p>}
                    <div className="flex items-center gap-2 mt-3">
                      <button onClick={() => act(u.username, 'deactivate')}
                        className="px-3 py-1.5 bg-amber-700 hover:bg-amber-600 text-white text-xs rounded-lg">
                        Pause access
                      </button>
                      <button onClick={() => { setConfirmDeact(null); setActErr(null); }}
                        className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded-lg">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                {confirmDelete === u.username && (
                  <div className="mt-3 pt-3 border-t border-gray-800">
                    <p className="text-sm text-gray-300">
                      Delete <span className="text-white font-medium">{userDisplayName(u)}</span>?
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Removes them from this list and from Workforce, ends the login and closes their
                      employment. It only affects things going forward.
                    </p>
                    {histFor[u.username] && (
                      histFor[u.username].total_rows > 0 ? (
                        <p className="text-xs text-gray-500 mt-2">
                          <span className="text-gray-300">Everything they did is kept</span> —{' '}
                          {Object.entries(histFor[u.username].history)
                            .map(([t, n]) => `${t.replace(/_/g, ' ')} ${n}`).join(' · ')}.
                          {' '}They still appear in reports for the months they worked, and their username
                          stays reserved for good.
                        </p>
                      ) : (
                        <p className="text-xs text-amber-400/80 mt-2">
                          This account has never done anything, so it will be removed outright.
                        </p>
                      )
                    )}
                    {actErr && <p className="text-xs text-red-400 mt-2">{actErr}</p>}
                    <div className="flex items-center gap-2 mt-3">
                      <button onClick={() => handleDelete(u.username)}
                        className="px-3 py-1.5 bg-red-700 hover:bg-red-600 text-white text-xs rounded-lg">
                        {histFor[u.username] && histFor[u.username].total_rows === 0
                          ? 'Delete account' : 'Delete, keep history'}
                      </button>
                      <button onClick={() => { setConfirmDelete(null); setActErr(null); }}
                        className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded-lg">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                {resetTarget === u.username && (
                  <div className="mt-3 pt-3 border-t border-gray-800 flex items-end gap-3">
                    <div className="flex-1">
                      <label className="text-gray-400 text-xs uppercase tracking-wider">
                        New password for {userDisplayName(u)}
                      </label>
                      <input type="password" value={resetPw} onChange={e => setResetPw(e.target.value)}
                        autoComplete="new-password" placeholder="Min 8 characters"
                        className="mt-1 w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500" />
                      {resetError && <p className="text-red-400 text-xs mt-1">{resetError}</p>}
                    </div>
                    <button onClick={() => handleResetPassword(u.username)}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg">
                      Set password
                    </button>
                    <button onClick={() => setResetTarget(null)}
                      className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg">
                      Cancel
                    </button>
                  </div>
                )}
                {editTarget === u.username && (
                  <div className="mt-3 pt-3 border-t border-gray-800 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <label className="text-gray-400 text-xs uppercase tracking-wider">Username</label>
                        <input value={editUsername} onChange={e => setEditUsername(e.target.value)}
                          autoComplete="off" maxLength={32}
                          className="mt-1 w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500" />
                        {editUsername.trim() !== u.username && (
                          <p className="text-amber-400/80 text-xs mt-1">
                            Renaming moves all of their history to the new name and signs them out.
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="text-gray-400 text-xs uppercase tracking-wider">First name</label>
                        <input value={editFirst} onChange={e => setEditFirst(e.target.value)}
                          className="mt-1 w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500" />
                      </div>
                      <div>
                        <label className="text-gray-400 text-xs uppercase tracking-wider">Last name</label>
                        <input value={editLast} onChange={e => setEditLast(e.target.value)}
                          className="mt-1 w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500" />
                      </div>
                      <div>
                        <label className="text-gray-400 text-xs uppercase tracking-wider">Email</label>
                        <input type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)}
                          autoComplete="off"
                          className="mt-1 w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500" />
                      </div>
                      <div>
                        <label className="text-gray-400 text-xs uppercase tracking-wider">Phone</label>
                        <input type="tel" value={editPhone} onChange={e => setEditPhone(e.target.value)}
                          autoComplete="off" placeholder="+372 …"
                          className="mt-1 w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500" />
                      </div>
                    </div>
                    {currentRole === 'admin' && (
                      <div>
                        <label className="text-gray-400 text-xs uppercase tracking-wider">Role</label>
                        <div className="mt-1 flex gap-2 flex-wrap">
                          {['staff', 'viewer', 'manager', 'admin'].map(r => (
                            <button key={r} onClick={() => setEditRole(r)}
                              className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${editRole === r ? 'bg-blue-600 border-blue-500 text-white' : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white'}`}>
                              {r.charAt(0).toUpperCase() + r.slice(1)}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {editError && <p className="text-red-400 text-sm">{editError}</p>}
                    <div className="flex gap-3">
                      <button onClick={() => handleSaveEdit(u.username)}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg">
                        Save changes
                      </button>
                      <button onClick={() => setEditTarget(null)}
                        className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {adding ? (
            <div className="bg-gray-900/50 rounded-xl border border-gray-700 p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-400 text-xs uppercase tracking-wider">Username</label>
                  <input value={newUsername} onChange={e => setNewUsername(e.target.value)}
                    placeholder="e.g. mari" autoComplete="off"
                    className="mt-1 w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500" />
                </div>
                <div>
                  <label className="text-gray-400 text-xs uppercase tracking-wider">Password</label>
                  <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                    placeholder="Set a password" autoComplete="new-password"
                    className="mt-1 w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500" />
                </div>
                <div>
                  <label className="text-gray-400 text-xs uppercase tracking-wider">First name</label>
                  <input value={newFirst} onChange={e => setNewFirst(e.target.value)}
                    placeholder="First name"
                    className="mt-1 w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500" />
                </div>
                <div>
                  <label className="text-gray-400 text-xs uppercase tracking-wider">Last name</label>
                  <input value={newLast} onChange={e => setNewLast(e.target.value)}
                    placeholder="Last name"
                    className="mt-1 w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500" />
                </div>
                <div>
                  <label className="text-gray-400 text-xs uppercase tracking-wider">Email</label>
                  <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)}
                    placeholder="email@motleydenim.com" autoComplete="off"
                    className="mt-1 w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500" />
                </div>
                <div>
                  <label className="text-gray-400 text-xs uppercase tracking-wider">Phone</label>
                  <input type="tel" value={newPhone} onChange={e => setNewPhone(e.target.value)}
                    placeholder="+372 …" autoComplete="off"
                    className="mt-1 w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500" />
                </div>
              </div>
              <div>
                <label className="text-gray-400 text-xs uppercase tracking-wider">Role</label>
                <div className="mt-1 flex gap-2 flex-wrap">
                  {roleOptions.map(r => (
                    <button key={r} onClick={() => setNewRole(r)}
                      className={`px-4 py-2 text-sm rounded-lg border transition-colors ${newRole === r ? 'bg-blue-600 border-blue-500 text-white' : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white'}`}>
                      {r.charAt(0).toUpperCase() + r.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              {/* An account is a login; a PERSON may hold several. Stated here rather than guessed
                  later from a matching email, which would make identity depend on editable
                  contact details. Admins only. */}
              {currentRole === 'admin' && (
                <div>
                  <label className="text-gray-400 text-xs uppercase tracking-wider">Who is this for</label>
                  <select value={newPrimary} onChange={e => setNewPrimary(e.target.value)}
                    className="mt-1 w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500">
                    <option value="">A new person</option>
                    {users.filter(u => !u.primary_username && !u.removed_at).map(u => (
                      <option key={u.username} value={u.username}>
                        Another login for {[u.first_name, u.last_name].filter(Boolean).join(' ') || u.username}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-gray-600 mt-1">
                    {newPrimary
                      ? 'A second login for someone already here — a warehouse-floor account, say. '
                        + 'They stay one person: one employment, one set of hours, and work done on '
                        + 'either account counts as theirs.'
                      : 'Creates a new person. Choose someone instead if this is a second login for '
                        + 'a person who already works here.'}
                  </p>
                </div>
              )}
              {addError && <p className="text-red-400 text-sm">{addError}</p>}
              <div className="flex gap-3">
                <button onClick={handleAdd}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg">
                  Create account
                </button>
                <button onClick={() => { setAdding(false); setAddError(''); }}
                  className="px-5 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => setAdding(true)}
              className="px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 border-dashed text-gray-400 hover:text-white text-sm rounded-xl w-full transition-colors">
              + Add account
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Settings page ─────────────────────────────────────────────────────────────


// ── Order Tabs management ─────────────────────────────────────────────────────

const TAB_COLORS = [
  { value: '',       label: 'Default' },
  { value: 'blue',   label: 'Blue'   },
  { value: 'green',  label: 'Green'  },
  { value: 'yellow', label: 'Yellow' },
  { value: 'orange', label: 'Orange' },
  { value: 'red',    label: 'Red'    },
  { value: 'purple', label: 'Purple' },
  { value: 'teal',   label: 'Teal'   },
  { value: 'pink',   label: 'Pink'   },
];

const TAB_SWATCH: Record<string, string> = {
  '': 'bg-gray-600', blue: 'bg-blue-600', green: 'bg-green-600',
  yellow: 'bg-yellow-500', orange: 'bg-orange-600', red: 'bg-red-600',
  purple: 'bg-purple-600', teal: 'bg-teal-600', pink: 'bg-pink-600',
};

interface TabCfg {
  key: string; label: string; position: number;
  color: string | null; is_system: boolean; count: number;
  urgent?: boolean;   // "handle first" — red count, notice, prompt before a new round
}

function TabsTab() {
  const [tabs, setTabs]       = React.useState<TabCfg[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [newLabel, setNewLabel] = React.useState('');
  const [newColor, setNewColor] = React.useState('');
  const [creating, setCreating] = React.useState(false);
  const [editingKey, setEditingKey] = React.useState<string | null>(null);
  const [editLabel, setEditLabel]   = React.useState('');
  const [editColor, setEditColor]   = React.useState('');
  const [saving, setSaving]   = React.useState(false);
  const [error, setError]     = React.useState('');

  const load = async () => {
    setLoading(true);
    const res = await authFetch('/api/tabs');
    if (res.ok) setTabs(await res.json());
    setLoading(false);
  };

  React.useEffect(() => { load(); }, []);

  const createTab = async () => {
    if (!newLabel.trim()) return;
    setSaving(true); setError('');
    const key = newLabel.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    const res = await authFetch('/api/tabs', {
      method: 'POST',
      body: JSON.stringify({ key, label: newLabel.trim(), color: newColor || null }),
    });
    if (res.ok) {
      setNewLabel(''); setNewColor(''); setCreating(false); load();
    } else {
      const d = await res.json().catch(() => ({}));
      setError(d.detail || 'Could not create tab');
    }
    setSaving(false);
  };

  const startEdit = (t: TabCfg) => {
    setEditingKey(t.key); setEditLabel(t.label); setEditColor(t.color || ''); setError('');
  };

  const saveEdit = async () => {
    if (!editingKey) return;
    setSaving(true); setError('');
    const res = await authFetch(`/api/tabs/${editingKey}`, {
      method: 'PUT',
      body: JSON.stringify({ label: editLabel, color: editColor || null }),
    });
    if (res.ok) { setEditingKey(null); load(); }
    else { const d = await res.json().catch(() => ({})); setError(d.detail || 'Could not save'); }
    setSaving(false);
  };

  const deleteTab = async (key: string, label: string) => {
    if (!window.confirm(`Delete tab "${label}"? Orders in this tab must be moved first.`)) return;
    const res = await authFetch(`/api/tabs/${key}`, { method: 'DELETE' });
    if (res.ok) { load(); }
    else { const d = await res.json().catch(() => ({})); alert(d.detail || 'Could not delete tab'); }
  };

  // Allowed on system tabs too: Product exchange is one. The backend gates it to admins.
  const toggleUrgent = async (t: TabCfg) => {
    setError('');
    const res = await authFetch(`/api/tabs/${t.key}`, {
      method: 'PUT', body: JSON.stringify({ urgent: !t.urgent }),
    });
    if (res.ok) load();
    else { const d = await res.json().catch(() => ({})); setError(d.detail || 'Could not change the tab'); }
  };

  const movePos = async (key: string, dir: -1 | 1) => {
    const idx = tabs.findIndex(t => t.key === key);
    if (idx < 0) return;
    const newOrder = [...tabs];
    const swap = idx + dir;
    if (swap < 0 || swap >= newOrder.length) return;
    [newOrder[idx], newOrder[swap]] = [newOrder[swap], newOrder[idx]];
    setTabs(newOrder);
    await authFetch('/api/tabs/reorder', {
      method: 'POST',
      body: JSON.stringify({ order: newOrder.map(t => t.key) }),
    });
  };

  if (loading) return <div className="text-gray-500 py-8 text-sm">Loading...</div>;

  return (
    <div className="max-w-xl">
      <p className="text-gray-500 text-sm mb-6">
        Create custom tabs to organise orders into queues (e.g. "Picker 3", "On hold").
        All tab movements are timestamped for statistics. System tabs cannot be renamed or deleted.
      </p>

      {error && <div className="mb-4 text-red-400 text-sm bg-red-900/20 border border-red-800/40 rounded-lg px-4 py-2">{error}</div>}

      <div className="space-y-2 mb-6">
        {tabs.map((t, idx) => (
          <div key={t.key}
            className="bg-[#1a1d27] border border-gray-800 rounded-xl px-4 py-3 flex items-center gap-3">

            {/* Colour swatch */}
            <span className={`w-3 h-3 rounded-full flex-shrink-0 ${TAB_SWATCH[t.color || ''] ?? 'bg-gray-600'}`} />

            {editingKey === t.key ? (
              <div className="flex items-center gap-2 flex-1 flex-wrap">
                <input value={editLabel} onChange={e => setEditLabel(e.target.value)}
                  className="bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:border-blue-500 w-44"
                  autoFocus onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditingKey(null); }} />
                <select value={editColor} onChange={e => setEditColor(e.target.value)}
                  className="bg-gray-800 border border-gray-700 text-gray-300 text-sm rounded-lg px-2 py-1.5 focus:outline-none focus:border-blue-500">
                  {TAB_COLORS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <button onClick={saveEdit} disabled={saving || !editLabel.trim()}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs rounded-lg">
                  Save
                </button>
                <button onClick={() => setEditingKey(null)}
                  className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 text-xs rounded-lg">
                  Cancel
                </button>
              </div>
            ) : (
              <>
                <span className="flex-1 text-white text-sm font-medium">{t.label}</span>
                {t.count > 0 && <span className="text-xs text-gray-500">{t.count} orders</span>}
                {t.is_system && <span className="text-xs text-gray-600 bg-gray-800 px-2 py-0.5 rounded">system</span>}
                <button onClick={() => toggleUrgent(t)}
                  title="Handle first: the count shows red, the tab says so above its table, and starting a new pick-and-pack round while these wait asks to pack them first"
                  className={`text-xs px-2 py-0.5 rounded ${t.urgent
                    ? 'bg-red-900/50 text-red-200 hover:bg-red-900/70'
                    : 'bg-gray-800 text-gray-500 hover:text-gray-300'}`}>
                  {t.urgent ? 'handle first' : 'normal'}
                </button>
              </>
            )}

            {editingKey !== t.key && (
              <div className="flex items-center gap-1 ml-auto flex-shrink-0">
                <button onClick={() => movePos(t.key, -1)} disabled={idx === 0}
                  className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-white disabled:opacity-20 rounded">
                  ↑
                </button>
                <button onClick={() => movePos(t.key, 1)} disabled={idx === tabs.length - 1}
                  className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-white disabled:opacity-20 rounded">
                  ↓
                </button>
                {!t.is_system && (
                  <>
                    <button onClick={() => startEdit(t)}
                      className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg">
                      Edit
                    </button>
                    <button onClick={() => deleteTab(t.key, t.label)}
                      className="px-2 py-1 text-xs bg-red-900/40 hover:bg-red-800/60 text-red-400 rounded-lg">
                      Delete
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {creating ? (
        <div className="bg-[#1a1d27] border border-gray-800 rounded-xl px-4 py-4 space-y-3">
          <p className="text-white text-sm font-medium">New tab</p>
          <div className="flex items-center gap-2 flex-wrap">
            <input placeholder="Tab name" value={newLabel} onChange={e => setNewLabel(e.target.value)}
              className="bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500 w-48"
              autoFocus onKeyDown={e => { if (e.key === 'Enter') createTab(); if (e.key === 'Escape') setCreating(false); }} />
            <select value={newColor} onChange={e => setNewColor(e.target.value)}
              className="bg-gray-800 border border-gray-700 text-gray-300 text-sm rounded-lg px-2 py-2 focus:outline-none focus:border-blue-500">
              {TAB_COLORS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <button onClick={createTab} disabled={saving || !newLabel.trim()}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm rounded-lg">
              {saving ? 'Creating...' : 'Create'}
            </button>
            <button onClick={() => { setCreating(false); setError(''); }}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm rounded-lg">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => { setCreating(true); setError(''); }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
          <span className="text-lg leading-none">+</span> New tab
        </button>
      )}
    </div>
  );
}

// ── General (warehouse timezone) ───────────────────────────────────────────────

const TZ_OPTIONS = [
  'Europe/Tallinn',
  'Europe/Stockholm',
  'Europe/Helsinki',
  'Europe/Riga',
  'Europe/Vilnius',
  'Europe/Oslo',
  'Europe/Copenhagen',
  'UTC',
];

function LabelPrintSection() {
  const [width, setWidth]         = useState<string>('102');
  const [height, setHeight]       = useState<string>('192');
  const [margin, setMargin]       = useState<string>('3');
  const [normalize, setNormalize] = useState(true);
  const [zebraDirect, setZebraDirect] = useState(true);
  const [dirty, setDirty]         = useState(false);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [saved, setSaved]         = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(`${API}/label-print-settings`);
        if (!res.ok) return;
        const d = await res.json();
        if (d.width_mm)  setWidth(String(d.width_mm));
        if (d.height_mm) setHeight(String(d.height_mm));
        if (d.margin_mm !== undefined) setMargin(String(d.margin_mm));
        setNormalize(d.normalize !== false);
        setZebraDirect(d.zebra_direct !== false);
      } catch { /* ignore */ }
    })();
  }, []);

  const save = async () => {
    setSaving(true); setError(''); setSaved(false);
    try {
      const res = await authFetch(`${API}/label-print-settings`, {
        method: 'PUT',
        body: JSON.stringify({
          width_mm: parseFloat(width),
          height_mm: parseFloat(height),
          margin_mm: parseFloat(margin),
          normalize,
          zebra_direct: zebraDirect,
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || 'Failed to save');
      } else {
        setSaved(true); setDirty(false);
      }
    } catch {
      setError('Failed to save');
    }
    setSaving(false);
  };

  return (
    <div className="space-y-3 mt-8">
      <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-1 px-1">Label printing</div>

      <div className="bg-[#1a1d27] rounded-xl border border-gray-800 px-5 py-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-white text-sm font-medium">Shipping label size</div>
            <div className="text-gray-500 text-xs mt-0.5">
              The label roll in the thermal printer. Every carrier label is scaled onto this page size when printing.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input type="number" min={20} max={300} value={width}
              onChange={e => { setWidth(e.target.value); setDirty(true); setSaved(false); }}
              className="w-20 bg-gray-800 border border-gray-700 text-white text-center text-sm rounded-lg px-2 py-2 focus:outline-none focus:border-blue-500" />
            <span className="text-gray-500 text-sm">×</span>
            <input type="number" min={20} max={400} value={height}
              onChange={e => { setHeight(e.target.value); setDirty(true); setSaved(false); }}
              className="w-20 bg-gray-800 border border-gray-700 text-white text-center text-sm rounded-lg px-2 py-2 focus:outline-none focus:border-blue-500" />
            <span className="text-gray-500 text-sm">mm</span>
            <button onClick={save} disabled={saving || !dirty}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between gap-4">
          <div>
            <div className="text-white text-sm font-medium">Safety margin</div>
            <div className="text-gray-500 text-xs mt-0.5">
              Blank border kept on every side so edge content (e.g. Omniva's border and phone number) isn't clipped
              by the printer. Increase if labels still cut off at the edge; decrease for a larger print.
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <input type="number" min={0} max={20} step={0.5} value={margin}
              onChange={e => { setMargin(e.target.value); setDirty(true); setSaved(false); }}
              className="w-20 bg-gray-800 border border-gray-700 text-white text-center text-sm rounded-lg px-2 py-2 focus:outline-none focus:border-blue-500" />
            <span className="text-gray-500 text-sm">mm</span>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between">
          <div>
            <div className="text-white text-sm font-medium">Unify label sizes</div>
            <div className="text-gray-500 text-xs mt-0.5">
              Scale every carrier's label to fill the size above. Turn off to print labels exactly as the carriers return them.
            </div>
          </div>
          <button
            onClick={() => { setNormalize(!normalize); setDirty(true); setSaved(false); }}
            className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${normalize ? 'bg-blue-600' : 'bg-gray-700'}`}
          >
            <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all duration-200 ${normalize ? 'left-7' : 'left-1'}`} />
          </button>
        </div>

        <div className="mt-3 pt-3 border-t border-gray-800 flex items-center justify-between">
          <div>
            <div className="text-white text-sm font-medium">Print directly to label printer</div>
            <div className="text-gray-500 text-xs mt-0.5">
              On computers running Zebra Browser Print, labels go straight to the printer with no tab or dialog.
              Turn off to always open the print preview in a tab (the safe fallback if the printer misbehaves).
            </div>
          </div>
          <button
            onClick={() => { setZebraDirect(!zebraDirect); setDirty(true); setSaved(false); }}
            className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${zebraDirect ? 'bg-blue-600' : 'bg-gray-700'}`}
          >
            <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all duration-200 ${zebraDirect ? 'left-7' : 'left-1'}`} />
          </button>
        </div>

        {error && <div className="mt-2 text-xs text-red-400">{error}</div>}
        {saved && <div className="mt-2 text-xs text-green-400">Saved. Applies from the next Print Labels.</div>}
      </div>
    </div>
  );
}

function GeneralTab() {
  const [currentTz, setCurrentTz] = useState<string>('');
  const [selected, setSelected]   = useState<string>('');
  const [history, setHistory]     = useState<any[]>([]);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [saved, setSaved]         = useState(false);

  const load = async () => {
    try {
      const res = await authFetch(`${API}/settings/timezone`);
      if (!res.ok) return;
      const data = await res.json();
      setCurrentTz(data.current_tz);
      setSelected(data.current_tz);
      setHistory(data.history || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true); setError(''); setSaved(false);
    try {
      const res = await authFetch(`${API}/settings/timezone`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tz: selected }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || 'Failed to save');
      } else {
        setSaved(true);
        await load();
      }
    } catch {
      setError('Failed to save');
    }
    setSaving(false);
  };

  const options = TZ_OPTIONS.includes(currentTz) || !currentTz
    ? TZ_OPTIONS : [currentTz, ...TZ_OPTIONS];
  const lastChange = history[0];

  return (
    <div>
      <p className="text-gray-500 text-sm mb-6">General warehouse configuration.</p>
      <div className="space-y-3">
        <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-1 px-1">Timezone</div>

        <div className="bg-[#1a1d27] rounded-xl border border-gray-800 px-5 py-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-white text-sm font-medium">Warehouse timezone</div>
              <div className="text-gray-500 text-xs mt-0.5">
                The timezone the warehouse currently operates in. Used to display work times.
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={selected}
                onChange={e => { setSelected(e.target.value); setSaved(false); }}
                className="bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
              >
                {options.map(tz => <option key={tz} value={tz}>{tz}</option>)}
              </select>
              <button
                onClick={save}
                disabled={saving || selected === currentTz}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>

          <div className="mt-3 text-xs text-amber-400/80 bg-amber-950/20 border border-amber-900/40 rounded-lg px-3 py-2">
            Only affects records created from now on. Past times keep the timezone they were
            recorded in — changing this never alters historical data.
          </div>

          {error && <div className="mt-2 text-xs text-red-400">{error}</div>}
          {saved && <div className="mt-2 text-xs text-green-400">Saved.</div>}

          {lastChange && (
            <div className="mt-3 text-xs text-gray-500">
              Current: <span className="text-gray-300">{currentTz}</span>
              {lastChange.effective_from && (
                <> · since {fmtWh(lastChange.effective_from, 'short')}</>
              )}
              {lastChange.changed_by && <> · by {lastChange.changed_by}</>}
            </div>
          )}
        </div>
      </div>

      <LabelPrintSection />
    </div>
  );
}

// ── Appearance (per-user theme + accent; admin: WMS logo) ────────────────────

function fileToDataUrl(file: File, maxBytes: number): Promise<string> {
  return new Promise((resolve, reject) => {
    if (file.size > maxBytes) { reject(new Error(`Image too large (max ${Math.round(maxBytes / 1024)} KB)`)); return; }
    if (!file.type.startsWith('image/')) { reject(new Error('Please choose an image file')); return; }
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error('Could not read file'));
    r.readAsDataURL(file);
  });
}

function AppearanceTab() {
  const role = localStorage.getItem('wms_role') || 'staff';
  const [prefs, setPrefs] = useState<AppearancePrefs>(() => cachedPrefs());
  const [logo, setLogo] = useState<string | null>(cachedLogo);
  const [favicon, setFavicon] = useState<string | null>(cachedFavicon);
  const [msg, setMsg] = useState('');
  const [logoMsg, setLogoMsg] = useState('');
  const [faviconMsg, setFaviconMsg] = useState('');

  useEffect(() => {
    (async () => {
      const r = await authFetch(`${API}/prefs`);
      if (r.ok) { const p = await r.json(); setPrefs(p); applyAppearance(p); }
    })();
  }, []);

  const update = async (patch: AppearancePrefs) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    applyAppearance(next);          // instant
    const r = await authFetch(`${API}/prefs`, { method: 'PUT', body: JSON.stringify({ prefs: next }) });
    setMsg(r.ok ? 'Saved' : 'Save failed');
    setTimeout(() => setMsg(''), 2000);
  };

  // One PUT per image. The endpoint merges the patch into the stored branding, so
  // saving the favicon cannot blank the logo (or the other way round).
  const saveBranding = async (patch: Branding) => {
    const r = await authFetch(`${API}/branding`, { method: 'PUT', body: JSON.stringify(patch) });
    if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error((d as any).detail || 'Save failed'); }
    storeBranding(patch);
    if ('wms_logo' in patch) setLogo(patch.wms_logo ?? null);
    if ('wms_favicon' in patch) setFavicon(patch.wms_favicon ?? null);
  };

  const uploadLogo = async (file: File | null) => {
    if (!file) return;
    setLogoMsg('');
    try {
      await saveBranding({ wms_logo: await fileToDataUrl(file, 300 * 1024) });
      setLogoMsg('Logo updated');
    } catch (e: any) {
      setLogoMsg(e.message || 'Upload failed');
    }
  };

  const removeLogo = async () => {
    try {
      await saveBranding({ wms_logo: null });
      setLogoMsg('Logo removed');
    } catch (e: any) {
      setLogoMsg(e.message || 'Remove failed');
    }
  };

  // The source file can be generous -- it is redrawn to a 128px square before saving,
  // so what actually gets stored and injected on every page load stays a few KB.
  const uploadFavicon = async (file: File | null) => {
    if (!file) return;
    setFaviconMsg('');
    try {
      const raw = await fileToDataUrl(file, 2 * 1024 * 1024);
      await saveBranding({ wms_favicon: await toSquarePng(raw) });
      setFaviconMsg('Favicon updated — reload the page to see it in the tab');
    } catch (e: any) {
      setFaviconMsg(e.message || 'Upload failed');
    }
  };

  const faviconFromLogo = async () => {
    if (!logo) return;
    setFaviconMsg('');
    try {
      await saveBranding({ wms_favicon: await toSquarePng(logo) });
      setFaviconMsg('Favicon set from the logo — reload the page to see it in the tab');
    } catch (e: any) {
      setFaviconMsg(e.message || 'Failed');
    }
  };

  const removeFavicon = async () => {
    try {
      await saveBranding({ wms_favicon: null });
      setFaviconMsg('Reverted to the default icon');
    } catch (e: any) {
      setFaviconMsg(e.message || 'Reset failed');
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-5">
        <div className="text-gray-400 text-xs uppercase tracking-widest font-semibold mb-1">Theme</div>
        <p className="text-gray-500 text-xs mb-4">Personal setting — every user picks their own.</p>
        <div className="flex gap-3">
          {(['dark', 'light'] as const).map(t => (
            <button key={t} onClick={() => update({ theme: t })}
              className={`px-5 py-3 rounded-xl border text-sm font-medium transition-colors ${
                (prefs.theme ?? 'dark') === t
                  ? 'border-blue-500 bg-blue-600 text-white'
                  : 'border-gray-700 bg-gray-800 text-gray-400 hover:text-white'
              }`}>
              {t === 'dark' ? '🌙 Dark' : '☀️ Light'}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-5">
        <div className="text-gray-400 text-xs uppercase tracking-widest font-semibold mb-1">Accent color</div>
        <p className="text-gray-500 text-xs mb-4">Colors buttons, active tabs and highlights.</p>
        <div className="flex gap-3 flex-wrap">
          {Object.entries(ACCENTS).map(([key, a]) => (
            <button key={key} onClick={() => update({ accent: key })} title={a.label}
              className={`w-10 h-10 rounded-full border-2 transition-transform hover:scale-110 ${
                (prefs.accent ?? 'blue') === key ? 'border-white scale-110' : 'border-transparent'
              }`}
              style={{ backgroundColor: a.main }} />
          ))}
        </div>
        {msg && <p className="text-green-400 text-sm mt-3">{msg}</p>}
      </div>

      {role === 'admin' && (
        <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-5">
          <div className="text-gray-400 text-xs uppercase tracking-widest font-semibold mb-1">WMS logo</div>
          <p className="text-gray-500 text-xs mb-4">
            Shown on the sign-in page and in the top-left header, for every user.
            PNG with transparency works best, max 300 KB.
          </p>
          <div className="flex items-center gap-4 flex-wrap">
            {logo
              ? <img src={logo} alt="WMS logo" className="h-12 max-w-[220px] object-contain bg-gray-800 rounded-lg p-1.5" />
              : <span className="text-gray-600 text-sm">No logo — the blue “W” is shown</span>}
            <label className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium rounded-lg cursor-pointer">
              Upload logo
              <input type="file" accept="image/*" className="hidden"
                onChange={e => uploadLogo(e.target.files?.[0] ?? null)} />
            </label>
            {logo && (
              <button onClick={removeLogo} className="px-4 py-2 text-gray-500 hover:text-red-400 text-sm">
                Remove
              </button>
            )}
          </div>
          {logoMsg && <p className="text-green-400 text-sm mt-3">{logoMsg}</p>}
        </div>
      )}

      {role === 'admin' && (
        <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-5">
          <div className="text-gray-400 text-xs uppercase tracking-widest font-semibold mb-1">Favicon</div>
          <p className="text-gray-500 text-xs mb-4">
            The icon in the browser tab and in saved bookmarks. Square images work best —
            whatever you upload is resized to 128×128. Fine detail turns to mush at tab size,
            so a simple mark reads better than a full logo.
          </p>
          <div className="flex items-center gap-4 flex-wrap">
            <img src={favicon || '/favicon.ico?v=2'} alt="Favicon"
              className="w-8 h-8 object-contain bg-gray-800 rounded p-1" />
            <span className="text-gray-600 text-sm">
              {favicon ? 'Custom icon' : 'Default icon (built from the WMS logo)'}
            </span>
            <label className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium rounded-lg cursor-pointer">
              Upload favicon
              <input type="file" accept="image/*" className="hidden"
                onChange={e => uploadFavicon(e.target.files?.[0] ?? null)} />
            </label>
            {logo && (
              <button onClick={faviconFromLogo} className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm rounded-lg">
                Use the WMS logo
              </button>
            )}
            {favicon && (
              <button onClick={removeFavicon} className="px-4 py-2 text-gray-500 hover:text-red-400 text-sm">
                Reset
              </button>
            )}
          </div>
          {faviconMsg && <p className="text-green-400 text-sm mt-3">{faviconMsg}</p>}
        </div>
      )}
    </div>
  );
}

// ── My profile ────────────────────────────────────────────────────────────────
// Everything about your own account in one place: the login name and password you sign in
// with, how you can be reached, and (read-only) the identity that payroll and statistics
// use. First/last name and role are shown but not editable -- those are what work hours and
// order history are reported under, so an admin changes them.

const ROLE_BLURB: Record<string, string> = {
  admin:   'Full access, including settings, users and shipping.',
  manager: 'Warehouse operations plus staff and viewer accounts.',
  staff:   'Day-to-day warehouse work: picking, packing, returns.',
  viewer:  'Read-only access.',
};

// The language the packing instructions are shown in (Fernando, 2026-09-28) — a per-person
// preference in user_prefs; on a tablet the flow uses the language of the person chosen in
// "Who is picking?". Read-merge-write: prefs also carries the appearance settings.
function LanguageCard() {
  const [lang, setLang] = useState<string>('en');
  const [msg, setMsg] = useState('');
  useEffect(() => {
    authFetch(`${API}/prefs`).then(r => r.ok ? r.json() : {}).then((p: any) => setLang(p?.lang || 'en')).catch(() => {});
  }, []);
  const save = async (v: string) => {
    setLang(v);
    const cur = await authFetch(`${API}/prefs`).then(r => r.ok ? r.json() : {}).catch(() => ({}));
    const res = await authFetch(`${API}/prefs`, { method: 'PUT', body: JSON.stringify({ prefs: { ...cur, lang: v } }) });
    setMsg(res.ok ? 'Saved' : 'Could not save');
    setTimeout(() => setMsg(''), 2000);
  };
  return (
    <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-5 flex items-center justify-between gap-4">
      <div>
        <div className="text-white text-sm font-medium">Language for packing instructions</div>
        <div className="text-gray-500 text-xs mt-0.5">What the packing screen tells you when an item is missing, when a label is missing, and which cart a parcel goes into.</div>
      </div>
      <div className="flex items-center gap-2">
        <select value={lang} onChange={e => save(e.target.value)}
          className="bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2">
          <option value="en">English</option><option value="et">Eesti</option><option value="ru">Русский</option>
        </select>
        <span className="text-xs text-gray-500 w-20">{msg}</span>
      </div>
    </div>
  );
}

function MyProfileTab() {
  const [me, setMe] = useState<{
    username: string; first_name: string; last_name: string;
    email: string; phone: string; role: string;
  } | null>(null);

  // Contact details
  const [email, setEmail]   = useState('');
  const [phone, setPhone]   = useState('');
  const [contactMsg, setContactMsg]     = useState('');
  const [contactError, setContactError] = useState('');

  // Username
  const [showUn, setShowUn]       = useState(false);
  const [unNew, setUnNew]         = useState('');
  const [unPassword, setUnPassword] = useState('');
  const [unError, setUnError]     = useState('');
  const [unBusy, setUnBusy]       = useState(false);

  // Password
  const [showCp, setShowCp]       = useState(false);
  const [cpCurrent, setCpCurrent] = useState('');
  const [cpNew, setCpNew]         = useState('');
  const [cpConfirm, setCpConfirm] = useState('');
  const [cpError, setCpError]     = useState('');
  const [cpOk, setCpOk]           = useState(false);

  useEffect(() => {
    authFetch(`${API}/me`).then(r => r.ok ? r.json() : null).then(d => {
      if (!d) return;
      setMe(d);
      setEmail(d.email || '');
      setPhone(d.phone || '');
      setUnNew(d.username || '');
    });
  }, []);

  const saveContact = async () => {
    setContactError(''); setContactMsg('');
    const res = await authFetch(`${API}/users/me/profile`, {
      method: 'PUT',
      body: JSON.stringify({ email, phone }),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      setContactError(d.detail || 'Could not save'); return;
    }
    setContactMsg('Saved');
    setTimeout(() => setContactMsg(''), 2000);
  };

  // A rename rewrites every history row and mints a new token, because the old one carries
  // the old name as its subject. Reload once the swap is stored so the header, statistics and
  // every open panel pick up the new identity together.
  const saveUsername = async () => {
    setUnError('');
    if (!unNew.trim())   { setUnError('Enter a new username'); return; }
    if (!unPassword)     { setUnError('Enter your password to confirm'); return; }
    setUnBusy(true);
    const res = await authFetch(`${API}/users/me/username`, {
      method: 'PUT',
      body: JSON.stringify({ new_username: unNew.trim(), password: unPassword }),
    });
    setUnBusy(false);
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      setUnError(d.detail || 'Could not change username'); return;
    }
    const d = await res.json();
    if (d.token) localStorage.setItem('wms_token', d.token);
    if (d.username) localStorage.setItem('wms_username', d.username);
    window.location.reload();
  };

  const changePassword = async () => {
    setCpError('');
    if (!cpCurrent || !cpNew) { setCpError('All fields required'); return; }
    if (cpNew !== cpConfirm)  { setCpError('New passwords do not match'); return; }
    if (cpNew.length < 8)     { setCpError('Password must be at least 8 characters'); return; }
    const res = await authFetch(`${API}/users/me/password`, {
      method: 'PUT',
      body: JSON.stringify({ current_password: cpCurrent, new_password: cpNew }),
    });
    const d = await res.json().catch(() => ({} as any));
    if (!res.ok) { setCpError(d.detail || 'Error'); return; }
    // the server ends every other session on a password change and hands THIS one a fresh token
    if (d.token) {
      localStorage.setItem('wms_token', d.token);
      if (d.session_id) localStorage.setItem('wms_session_id', String(d.session_id));
    }
    setCpOk(true);
    setCpCurrent(''); setCpNew(''); setCpConfirm('');
    setTimeout(() => { setCpOk(false); setShowCp(false); }, 2000);
  };

  const inputCls = 'mt-1 w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500';
  const labelCls = 'text-gray-400 text-xs uppercase tracking-wider';

  if (!me) return <div className="text-gray-500 text-sm">Loading…</div>;

  const fullName = [me.first_name, me.last_name].filter(Boolean).join(' ');

  return (
    <div className="space-y-6 max-w-3xl">

      <LanguageCard />

      {/* Identity — read-only */}
      <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-5">
        <div className="text-white text-sm font-medium">Who you are</div>
        <div className="grid grid-cols-3 gap-3 mt-4">
          <div>
            <label className={labelCls}>First name</label>
            <div className="mt-1 text-white text-sm px-3 py-2 bg-gray-900/60 border border-gray-800 rounded-lg">
              {me.first_name || <span className="text-gray-600">—</span>}
            </div>
          </div>
          <div>
            <label className={labelCls}>Last name</label>
            <div className="mt-1 text-white text-sm px-3 py-2 bg-gray-900/60 border border-gray-800 rounded-lg">
              {me.last_name || <span className="text-gray-600">—</span>}
            </div>
          </div>
          <div>
            <label className={labelCls}>Role</label>
            <div className="mt-1 px-3 py-2 bg-gray-900/60 border border-gray-800 rounded-lg">
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${ROLE_COLORS[me.role] || ROLE_COLORS.staff}`}>
                {me.role}
              </span>
            </div>
          </div>
        </div>
        <p className="text-gray-500 text-xs mt-3">
          Your name{fullName ? ` (${fullName})` : ''} and role are what your work hours and order
          history are reported under. Ask an admin if either needs changing.
        </p>
        {ROLE_BLURB[me.role] && (
          <p className="text-gray-600 text-xs mt-1">{ROLE_BLURB[me.role]}</p>
        )}
      </div>

      {/* Contact details */}
      <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-5">
        <div className="text-white text-sm font-medium">Contact details</div>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div>
            <label className={labelCls}>Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              autoComplete="email" placeholder="name@motleydenim.com" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Phone</label>
            <input type="tel" value={phone} onChange={e => setPhone(e.target.value)}
              autoComplete="tel" placeholder="+372 …" className={inputCls} />
          </div>
        </div>
        {contactError && <p className="text-red-400 text-sm mt-3">{contactError}</p>}
        <div className="flex items-center gap-3 mt-4">
          <button onClick={saveContact}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg">
            Save
          </button>
          {contactMsg && <span className="text-green-400 text-sm">{contactMsg}</span>}
        </div>
      </div>

      {/* Username */}
      <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-white text-sm font-medium">Username</div>
            <div className="text-gray-500 text-xs mt-0.5">You sign in as {me.username}</div>
          </div>
          {!showUn && (
            <button onClick={() => { setShowUn(true); setUnNew(me.username); setUnError(''); }}
              className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm rounded-lg transition-colors">
              Change username
            </button>
          )}
        </div>
        {showUn && (
          <div className="mt-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>New username</label>
                <input value={unNew} onChange={e => setUnNew(e.target.value)}
                  autoComplete="off" maxLength={32} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Confirm with your password</label>
                <input type="password" value={unPassword} onChange={e => setUnPassword(e.target.value)}
                  autoComplete="current-password" className={inputCls} />
              </div>
            </div>
            <p className="text-gray-500 text-xs">
              Letters, numbers, space, dot, dash and underscore. Your picking, packing and work-hour
              history moves with you. Everywhere you are signed in — including this device — will
              ask you to sign in again with the new name.
            </p>
            {unError && <p className="text-red-400 text-sm">{unError}</p>}
            <div className="flex gap-3">
              <button onClick={saveUsername} disabled={unBusy}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg">
                {unBusy ? 'Changing…' : 'Change username'}
              </button>
              <button onClick={() => { setShowUn(false); setUnError(''); setUnPassword(''); }}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Password — moved here from the Users tab, where it sat behind an admin-only list */}
      <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-white text-sm font-medium">Password</div>
            <div className="text-gray-500 text-xs mt-0.5">At least 8 characters. Changing it signs out your other devices.</div>
          </div>
          {!showCp && (
            <button onClick={() => setShowCp(true)}
              className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm rounded-lg transition-colors">
              Change password
            </button>
          )}
        </div>
        {showCp && (
          <div className="mt-4 space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>Current password</label>
                <input type="password" value={cpCurrent} onChange={e => setCpCurrent(e.target.value)}
                  autoComplete="current-password" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>New password</label>
                <input type="password" value={cpNew} onChange={e => setCpNew(e.target.value)}
                  autoComplete="new-password" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Confirm new</label>
                <input type="password" value={cpConfirm} onChange={e => setCpConfirm(e.target.value)}
                  autoComplete="new-password" className={inputCls} />
              </div>
            </div>
            {cpError && <p className="text-red-400 text-sm">{cpError}</p>}
            {cpOk    && <p className="text-green-400 text-sm">Password updated!</p>}
            <div className="flex gap-3">
              <button onClick={changePassword}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg">
                Update password
              </button>
              <button onClick={() => { setShowCp(false); setCpError(''); }}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}

const ALL_SETTINGS_TABS = [
  { key: 'general',   label: 'General',    minRole: 'admin' },
  { key: 'stores',    label: 'Stores',     minRole: 'admin' },
  // Staff since 2026-09-16 (Fernando): the warehouse workers handle their own
  // aisles, walk and floor plan. Backend gate: WAREHOUSE_EDIT_ROLES.
  { key: 'warehouse', label: 'Warehouse',  minRole: 'staff' },
  { key: 'packing',   label: 'Packing',    minRole: 'manager' },
  // Users used to be visible to everyone only because it hosted the "my password" box. That
  // now lives in My profile, and the rest of the tab is the admin/manager account list.
  { key: 'users',     label: 'Users',      minRole: 'manager' },
  // Guided P&P (2026-10-17): badges, PINs, per-person rights, the station/tablet registry. Admin edits, managers look.
  { key: 'badges',    label: 'Badges & rights', minRole: 'manager' },
  // Guided P&P phase 2: the station computers' print agents, which printer is the A4 / each station's Zebra.
  { key: 'printing',  label: 'Printing',   minRole: 'manager' },
  // the announcement bar (2026-10-17): managers and admins post, everyone sees it
  { key: 'announcements', label: 'Announcements', minRole: 'manager' },
  { key: 'hrx', label: 'HRX & Bring', minRole: 'manager' },
  // Inbounds (2026-10-29): the numbered boxes per station, the cart, tap/scan at the shelf, the inbounds@ reader
  { key: 'inbound', label: 'Inbounds', minRole: 'manager' },
  { key: 'tabs',      label: 'Order Tabs', minRole: 'admin'  },
  { key: 'shipping',  label: 'Shipping',   minRole: 'admin'  },
  { key: 'connectors', label: 'Connectors', minRole: 'admin' },
  { key: 'notifications', label: 'Notifications', minRole: 'manager' },
  { key: 'customer_mails', label: 'Customer mails', minRole: 'manager' },
  { key: 'appearance', label: 'Appearance', minRole: 'viewer' },
  { key: 'profile',   label: 'My profile', minRole: 'viewer' },
];

const ROLE_ORDER = ['viewer', 'staff', 'manager', 'admin'];
function tabAllowed(minRole: string, userRole: string): boolean {
  return ROLE_ORDER.indexOf(userRole) >= ROLE_ORDER.indexOf(minRole);
}

export default function SettingsPage(props: { section?: string; sub?: string } = {}) {
  const raw = useParams<{ section: string; sub?: string; aisleId?: string }>();
  const params = { ...raw, section: props.section || raw.section, sub: props.sub || raw.sub };
  const section = params.section || 'stores';
  // Settings → Warehouse has its own tabs (Tools · Aisles · Smart Picking · Floor Plan, 2026-09-29)
  // and a page per aisle: /settings/warehouse/<tab> and /settings/warehouse/aisles/<name>
  const whSub: WarehouseSub = (WAREHOUSE_TABS.some(t => t.key === params.sub) ? params.sub : 'tools') as WarehouseSub;
  const bleed = section === 'warehouse' && whSub === 'floor';
  const navigate = useNavigate();
  const role = localStorage.getItem('wms_role') || 'staff';
  // a warehouse computer (station login) also sees Printing: its own printers, the setup behind an admin's PIN (21.10)
  const isDevice = !!localStorage.getItem('wms_is_device');
  const visibleTabs = ALL_SETTINGS_TABS.filter(t => tabAllowed(t.minRole, role) || (isDevice && t.key === 'printing'));

  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.some(t => t.key === section)) {
      navigate(`/settings/${visibleTabs[0].key}`, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  return (
    <div className={bleed ? 'p-3 sm:p-4' : 'p-6 max-w-5xl'}>
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-white">Settings</h1>
        <p className="text-gray-500 text-sm mt-0.5">Warehouse configuration</p>
      </div>

      <div className="flex gap-1 border-b border-gray-800 mb-6">
        {visibleTabs.map(t => (
          <button key={t.key} onClick={() => navigate(`/settings/${t.key}`)}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              section === t.key ? 'text-white border-b-2 border-blue-500' : 'text-gray-400 hover:text-white'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {section === 'general'   && <><GeneralTab /><OrderFlaggingCard /><LabelIssueFixesCard /></>}
      {section === 'stores'    && <StoresPage />}
      {section === 'warehouse' && <WarehouseSettings sub={whSub} aisleId={params.aisleId} />}
      {section === 'packing'   && <PackingTab />}
      {section === 'users'     && <UsersTab />}
      {section === 'badges'    && <BadgesRightsSettings />}
      {section === 'printing'  && <PrintingSettings />}
      {section === 'announcements' && <AnnouncementsSettings />}
      {section === 'hrx' && <HrxSettings />}
      {section === 'inbound' && <InboundSettingsCard />}
      {section === 'tabs'      && <TabsTab />}
      {section === 'shipping'  && <ShippingSettings />}
      {section === 'connectors' && <ConnectorsTab />}
      {section === 'notifications' && <NotificationsTab />}
      {section === 'customer_mails' && <CustomerMailsTab />}
      {section === 'appearance' && <AppearanceTab />}
      {section === 'profile'   && <MyProfileTab />}
    </div>
  );
}


// ── Order flagging (Fernando, 2026-09-29) — the rules that pull suspicious orders out of
// picking into Flagged orders (docs/FLAGGED-ORDERS.md). Backend: order_flags.py, app_settings
// 'order_flagging'. Windows and thresholds are data-backed defaults; tune here, no deploy.
// Customer-contact plan, phase 2 (2026-10-15): a contact fix buys the label at once. Without it a customer-support
// correction waits for someone to press Print, a fix made in the shop on an order whose label failed is never
// noticed, and an older correction overrides a newer value the shop holds. backend/main.py label-issue-fixes.
function LabelIssueFixesCard() {
  const [s, setS] = useState<{ on: boolean; set_by?: string; updated_at?: string } | null>(null);
  const [err, setErr] = useState('');
  const load = () => authFetch(`${API}/settings/label-issue-fixes`).then(r => r.json()).then(setS).catch(() => setErr('Could not load'));
  useEffect(() => { load(); }, []);
  const set = async (on: boolean) => {
    setErr('');
    const r = await authFetch(`${API}/settings/label-issue-fixes`, { method: 'PUT', body: JSON.stringify({ on }) });
    if (!r.ok) { setErr((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`); return; }
    load();
  };
  return (
    <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-5 mt-6">
      <div className="text-gray-400 text-xs uppercase tracking-widest font-semibold mb-2">Label issue</div>
      <label className="flex items-start gap-2 text-sm text-gray-200">
        <input type="checkbox" className="mt-1" checked={!!s?.on} disabled={!s} onChange={e => set(e.target.checked)} />
        <span>A contact fix buys the label at once
          <span className="block text-xs text-gray-500 mt-0.5">On an order whose label failed: a correction under "Customer gave wrong details…", or a
            fix made in the shop (seen at the next sync), requests the label straight away — the packer sees "Label ready". A fix made in the
            shop also wins over an older correction in the WMS.</span></span>
      </label>
      {s?.set_by && <div className="text-xs text-gray-500 mt-2">Last set by {s.set_by}{s.updated_at ? ` · ${new Date(s.updated_at).toLocaleString()}` : ''}</div>}
      {err && <div className="text-xs text-red-400 mt-2">{err}</div>}
    </div>
  );
}

function OrderFlaggingCard() {
  const [data, setData] = useState<any | null>(null);
  const [s, setS] = useState<any | null>(null);
  const [msg, setMsg] = useState('');
  const load = () => authFetch(`${API}/settings/order-flagging`).then(r => r.ok ? r.json() : null)
    .then(d => { if (d) { setData(d); setS(d.settings); } }).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    const r = await authFetch(`${API}/settings/order-flagging`, { method: 'PUT', body: JSON.stringify(s) });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setMsg(d.detail || `Could not save (HTTP ${r.status})`); return; }
    setMsg('✓ Saved — applies from the next sync (within a minute).'); load();
  };
  const untrust = async (id: number) => {
    if (!window.confirm('Remove this trust entry? The rules apply to the customer again.')) return;
    const r = await authFetch(`${API}/settings/order-flagging/trust/${id}`, { method: 'DELETE' });
    if (!r.ok) { setMsg(`Could not remove (HTTP ${r.status})`); return; }
    load();
  };
  if (!s) return null;
  const num = (k: string, label: string, hint: string) => (
    <label className="text-xs text-gray-500">{label}
      <input type="number" value={s[k]} onChange={e => setS({ ...s, [k]: Number(e.target.value) })} title={hint}
        className="keep-width w-24 ml-2 px-2 py-1 rounded bg-[#0f1117] border border-gray-700 text-white text-sm text-right" /></label>
  );
  const RULES: [string, string, string][] = [
    ['known', 'Known unclaimed-COD customer', 'e-mail/phone matches an unclaimed COD case, a COD parcel on its way back, or the Packeta history — their COD orders are flagged until a prepaid order is delivered (48 % of their later COD parcels come back)'],
    ['identical', 'Identical order', 'same customer, same lines, same delivery (method, address, pickup point), within the window — the later one is flagged; a re-order to another address is a correction and passes'],
    ['triple', '3rd COD order in the window', 'a 2nd COD order always passes (23 % come back, ≈ average); a 3rd within the window, counting the earlier ones, is flagged (61 %)'],
    ['names', 'One e-mail/phone, many names', 'COD orders of a customer who ordered under 3+ different names (60 % come back)'],
    ['cod_limit', 'COD over the carrier\'s limit', 'the carrier refused the cash-on-delivery amount when the label was bought (BG823: 862 € on Speedy) — flagged, and deleted when the store deletes flagged COD orders'],
  ];
  return (
    <div className="bg-[#1a1d27] rounded-xl border border-gray-800 px-5 py-4 mt-6 max-w-3xl">
      <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-2">Order flagging</div>
      <p className="text-gray-500 text-sm mb-3">Suspicious orders are moved to <span className="text-gray-300">Flagged orders</span> right after the sync, before a label is bought. CS contacts the customer and releases; a manager can trust a customer. Prepaid multi-orders are never flagged (their return rate equals single orders). {data?.flagged_now ? <span className="text-red-300">{data.flagged_now} flagged now.</span> : null}</p>
      <div className="grid gap-2 md:grid-cols-2 mb-3">
        {RULES.map(([k, label, hint]) => (
          <label key={k} className="flex items-center gap-2 text-sm text-gray-300" title={hint}>
            <input type="checkbox" checked={!!s.rules?.[k]} onChange={e => setS({ ...s, rules: { ...s.rules, [k]: e.target.checked } })} /> {label}
          </label>
        ))}
      </div>
      <div className="flex flex-wrap gap-4 mb-3">
        {num('triple_hours', 'Triple window (h)', 'a 3rd COD order within this many hours is flagged')}
        {num('identical_hours', 'Identical window (h)', '')}
        {num('names_threshold', 'Names threshold', 'different recipient names on one e-mail/phone')}
      </div>
      <div className="flex flex-wrap gap-4 mb-3">
        {(data?.stores || []).map((st: any) => (
          <label key={st.id} className="flex items-center gap-2 text-sm text-gray-300">
            <input type="checkbox" checked={s.stores?.[st.id] !== false} onChange={e => setS({ ...s, stores: { ...s.stores, [st.id]: e.target.checked } })} /> {st.name}
          </label>
        ))}
      </div>
      {/* Fernando, 2026-10-12: no workforce for flagged customers — a flagged COD order is deleted
          (cancelled in the shop) right after the flag, with a note. Per store; Shopify cannot. */}
      <div className="border-t border-gray-800 pt-3 mb-3">
        <div className="text-gray-300 text-sm font-medium mb-1">Delete flagged cash-on-delivery orders automatically</div>
        <p className="text-gray-500 text-xs mb-2">Right after the flag: cancelled in the shop (the shop returns the stock), then deleted here with a note saying it was flagged. A prepaid flagged order stays in Flagged — deleting it would need a refund.</p>
        <div className="flex flex-wrap items-center gap-4 mb-2">
          {(data?.stores || []).map((st: any) => (
            <label key={st.id} className={`flex items-center gap-2 text-sm ${st.can_cancel ? 'text-gray-300' : 'text-gray-600'}`}
              title={st.can_cancel ? '' : `${st.why_not} — its flagged orders stay in Flagged`}>
              <input type="checkbox" disabled={!st.can_cancel} checked={!!s.auto_delete?.[st.id]}
                onChange={e => setS({ ...s, auto_delete: { ...s.auto_delete, [st.id]: e.target.checked } })} /> {st.name}{st.can_cancel ? '' : ' — not possible'}
            </label>
          ))}
          {num('auto_delete_daily_cap', 'Daily limit per store', 'automatic deletes per store in 24 h; more wait in Flagged and show in Review')}
        </div>
        {Object.keys(data?.auto_deleted_24h || {}).length > 0 && (
          <div className="text-xs text-gray-500 mb-1">Deleted automatically in the last 24 h: {Object.entries(data.auto_deleted_24h).map(([k, v]) => `${(data.stores || []).find((x: any) => x.id === k)?.name || k} ${v}`).join(', ')}</div>
        )}
        {(data?.auto_delete_plan || []).length > 0 && (
          <div className="text-xs text-gray-400">
            <div className="mb-1 text-gray-500">In Flagged now — with the store's switch on:</div>
            {data.auto_delete_plan.map((p: any) => (
              <div key={p.order_id} className="flex gap-3 py-0.5 border-t border-gray-800">
                <span className="font-mono w-20 flex-none">{p.order_number}</span>
                <span className="w-28 flex-none text-gray-500">{(data.stores || []).find((x: any) => x.id === p.store)?.name || p.store}</span>
                <span className={p.action === 'delete' ? 'text-red-300' : 'text-gray-500'}>{p.action === 'delete' ? 'deleted' : `stays — ${p.why.join('; ')}`}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <button onClick={save} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg">Save</button>
      {msg && <div className={`text-xs mt-2 ${msg.startsWith('✓') ? 'text-green-400' : 'text-red-400'}`}>{msg}</div>}
      {(data?.trusted || []).length > 0 && (
        <div className="mt-4">
          <div className="text-gray-500 text-xs uppercase tracking-wider mb-1">Trusted customers</div>
          {data.trusted.map((t: any) => (
            <div key={t.id} className="flex items-center gap-3 text-xs text-gray-300 py-1 border-t border-gray-800">
              <span className="font-mono">{t.email || t.phone}</span><span className="text-gray-500 flex-1">{t.note} · {t.created_by}</span>
              <button onClick={() => untrust(t.id)} className="text-gray-500 hover:text-red-300">remove</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
